import React, { useState, useEffect } from 'react';
import './SheetSetupDialog.css';

function SheetSetupDialog({ headers, markerCount, onComplete, onCancel, initialConfig = null, disableEditableColumns = false }) {
  const [latColumn, setLatColumn] = useState('');
  const [lngColumn, setLngColumn] = useState('');
  const [styleColumn, setStyleColumn] = useState('');
  const [editableColumns, setEditableColumns] = useState(new Set());
  const [viewableColumns, setViewableColumns] = useState(new Set());
  const [titleColumn, setTitleColumn] = useState('');
  const [labelColumn, setLabelColumn] = useState('');
  const [loading, setLoading] = useState(false);

  // Auto-detect and pre-select columns, or use initialConfig if provided
  useEffect(() => {
    // If initialConfig is provided, use it instead of auto-detection
    if (initialConfig) {
      setLatColumn(initialConfig.latColumn || '');
      setLngColumn(initialConfig.lngColumn || '');
      setStyleColumn(initialConfig.styleColumn || '');
      setTitleColumn(initialConfig.titleColumn || '');
      setLabelColumn(initialConfig.labelColumn || '');
      setEditableColumns(new Set(initialConfig.editableColumns || []));
      setViewableColumns(new Set(initialConfig.viewableColumns || []));
      return;
    }

    // Otherwise, auto-detect columns
    const headerLower = headers.map(h => h.toLowerCase().trim());

    // Auto-detect longitude first (more specific pattern)
    // Match 'longitude', 'lng', or 'lon' as whole words or parts of compound words
    const lngIdx = headerLower.findIndex(h =>
      /\b(longitude|lng|lon)\b|long(?!-related)/.test(h)
    );
    if (lngIdx !== -1) setLngColumn(headers[lngIdx]);

    // Auto-detect latitude - match 'latitude' or 'lat' but not as part of unrelated words
    // Exclude headers that contain longitude-related terms
    const latIdx = headerLower.findIndex(h => {
      const hasLong = /\b(longitude|lng|lon)\b|long(?!-related)/.test(h);
      if (hasLong) return false;

      // Match 'latitude' or 'lat' as whole words or at start of compound words (like 'site latitude')
      const isLatitude = /\b(latitude|lat)\b|lat(?=itude)/.test(h);
      return isLatitude;
    });
    if (latIdx !== -1) setLatColumn(headers[latIdx]);

    // Auto-detect title
    const titleIdx = headerLower.findIndex(h =>
      h.includes('name') || h.includes('title') || h.includes('site')
    );
    if (titleIdx !== -1) setTitleColumn(headers[titleIdx]);

    // Auto-detect style column (color or status)
    const styleIdx = headerLower.findIndex(h =>
      h.includes('color') || h.includes('status') || h.includes('type')
    );
    if (styleIdx !== -1) setStyleColumn(headers[styleIdx]);

    // By default, all columns viewable except latitude/longitude, none editable
    setEditableColumns(new Set()); // No columns editable by default

    // Filter out latitude and longitude columns from viewable by default
    const defaultViewable = headers.filter(h => {
      const hLower = h.toLowerCase().trim();
      const isLat = latIdx !== -1 && h === headers[latIdx];
      const isLng = lngIdx !== -1 && h === headers[lngIdx];
      return !isLat && !isLng;
    });
    setViewableColumns(new Set(defaultViewable));
  }, [headers, initialConfig]);

  const toggleEditable = (header) => {
    setEditableColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(header)) {
        newSet.delete(header);
      } else {
        newSet.add(header);
        // If a column is editable, it must also be viewable
        setViewableColumns(prevView => new Set([...prevView, header]));
      }
      return newSet;
    });
  };

  const toggleViewable = (header) => {
    // Don't allow toggling if the field is editable
    if (editableColumns.has(header)) {
      return;
    }

    setViewableColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(header)) {
        newSet.delete(header);
      } else {
        newSet.add(header);
      }
      return newSet;
    });
  };

  const selectAllEditable = () => {
    const allHeaders = new Set(headers);
    setEditableColumns(allHeaders);
    setViewableColumns(prevView => new Set([...prevView, ...allHeaders]));
  };

  const deselectAllEditable = () => {
    setEditableColumns(new Set());
  };

  const selectAllViewable = () => {
    setViewableColumns(new Set(headers));
  };

  const deselectAllViewable = () => {
    setViewableColumns(new Set());
    setEditableColumns(new Set()); // Clear editable too since you can't edit what you can't see
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!latColumn || !lngColumn) {
      alert('Latitude and Longitude columns are required!');
      return;
    }

    setLoading(true);

    onComplete({
      latColumn,
      lngColumn,
      styleColumn,
      titleColumn,
      labelColumn,
      editableColumns: Array.from(editableColumns),
      viewableColumns: Array.from(viewableColumns),
    });
  };

  return (
    <div className="sheet-setup-overlay">
      <div className="sheet-setup-dialog">
        <h2>Configure Your Map</h2>
        <p className="setup-description">
          {markerCount > 0
            ? `${markerCount.toLocaleString()} markers found! Configure how to display and edit them:`
            : 'Configure your sheet columns to load markers:'}
        </p>

        <form onSubmit={handleSubmit}>
          <div className="setup-section">
            <h3>📍 Location Columns (Required)</h3>

            <div className="setup-field required">
              <label>Latitude Column</label>
              <select
                value={latColumn}
                onChange={(e) => setLatColumn(e.target.value)}
                required
              >
                <option value="">-- Select --</option>
                {headers.map(h => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
            </div>

            <div className="setup-field required">
              <label>Longitude Column</label>
              <select
                value={lngColumn}
                onChange={(e) => setLngColumn(e.target.value)}
                required
              >
                <option value="">-- Select --</option>
                {headers.map(h => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="setup-section">
            <h3>🎨 Display Options</h3>

            <div className="setup-field">
              <label>Title Column (shown in sidebar)</label>
              <select
                value={titleColumn}
                onChange={(e) => setTitleColumn(e.target.value)}
              >
                <option value="">-- None --</option>
                {headers.map(h => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
            </div>

            <div className="setup-field">
              <label>Label Column (text on map)</label>
              <select
                value={labelColumn}
                onChange={(e) => setLabelColumn(e.target.value)}
              >
                <option value="">-- None (no labels) --</option>
                {headers.map(h => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
              <small>Text displayed beside markers on the map</small>
            </div>

            <div className="setup-field">
              <label>Style Column (for color/grouping)</label>
              <select
                value={styleColumn}
                onChange={(e) => setStyleColumn(e.target.value)}
              >
                <option value="">-- None (all same color) --</option>
                {headers.map(h => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
              <small>Markers will be colored based on this column's values</small>
            </div>
          </div>

          <div className="setup-section columns-side-by-side">
            <div className="column-section">
              <div className="section-header-with-actions">
                <div>
                  <h3>👁️ Viewable Columns</h3>
                  <p className="section-description">
                    Select which columns are visible in the sidebar:
                  </p>
                </div>
                <div className="select-buttons">
                  <button type="button" onClick={selectAllViewable} className="btn-select-all">
                    Select All
                  </button>
                  <button type="button" onClick={deselectAllViewable} className="btn-deselect-all">
                    Deselect All
                  </button>
                </div>
              </div>

              <div className="column-checklist">
                {headers.map(header => {
                  const isEditable = editableColumns.has(header);
                  return (
                    <label
                      key={header}
                      className={`checkbox-label ${isEditable ? 'locked' : ''}`}
                      title={isEditable ? 'Editable columns are automatically viewable' : ''}
                    >
                      <input
                        type="checkbox"
                        checked={viewableColumns.has(header)}
                        onChange={() => toggleViewable(header)}
                        disabled={isEditable}
                      />
                      <span>{header}</span>
                      {isEditable && (
                        <span className="badge editable">Editable</span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>

            <div className={`column-section ${disableEditableColumns ? 'disabled' : ''}`}>
              <div className="section-header-with-actions">
                <div>
                  <h3>✏️ Editable Columns</h3>
                  <p className="section-description">
                    {disableEditableColumns
                      ? 'Editing is not available for this data source'
                      : 'Select which columns users can edit on the map:'}
                  </p>
                </div>
                {!disableEditableColumns && (
                  <div className="select-buttons">
                    <button type="button" onClick={selectAllEditable} className="btn-select-all">
                      Select All
                    </button>
                    <button type="button" onClick={deselectAllEditable} className="btn-deselect-all">
                      Deselect All
                    </button>
                  </div>
                )}
              </div>

              <div className="column-checklist">
                {headers.map(header => (
                  <label key={header} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editableColumns.has(header)}
                      onChange={() => toggleEditable(header)}
                      disabled={disableEditableColumns}
                    />
                    <span>{header}</span>
                    {(header === latColumn || header === lngColumn) && (
                      <span className="badge">Coordinates</span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="setup-actions">
            <button type="button" onClick={onCancel} className="btn-cancel" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={loading || !latColumn || !lngColumn}>
              {loading ? 'Generating...' : 'Apply & Show Map'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SheetSetupDialog;
